import React from 'react';
import { useHistory } from 'react-router-dom';
import { CommandGroup, CommandItem } from '@dashboard/components/ui/command';
import { useCommandPalette } from './CommandPaletteContext';
import { trackResultClick } from './telemetry';
import { useModifierOpen } from './useModifierOpen';
import { renderWithHighlight } from './highlight';
import type { StaticEntry, ActionEntry, ActionContext } from './registries/types';

interface Props {
  heading: string;
  groupKey: string;
  entries: (StaticEntry | ActionEntry)[];
  getCtx?: () => ActionContext;
  query?: string;
}

function isAction(entry: StaticEntry | ActionEntry): entry is ActionEntry {
  return typeof (entry as ActionEntry).run === 'function';
}

function matches(query: string, entry: { label: string; keywords?: string[] }) {
  if (!query) return true;
  const hay = [entry.label, ...(entry.keywords || [])].join(' ').toLowerCase();
  const q = query.toLowerCase().trim();
  return q.split(/\s+/).every(tok => hay.includes(tok));
}

export function PaletteStaticGroup({ heading, groupKey, entries, getCtx, query }: Props) {
  const history = useHistory();
  const { closePalette } = useCommandPalette();
  const modRef = useModifierOpen();

  const visible = entries.filter(e => matches(query ?? '', e));
  if (!visible.length) return null;

  return (
    <CommandGroup heading={heading}>
      {visible.map((entry, idx) => {
        const Icon = entry.icon;
        const keywordString = [entry.label, ...(entry.keywords || [])].join(' ');
        return (
          <CommandItem
            key={entry.id}
            value={keywordString}
            onSelect={() => {
              if (!isAction(entry)) {
                trackResultClick({ group: groupKey, rank: idx, queryLength: query?.length ?? 0 });
              }
              closePalette();
              if (isAction(entry)) {
                entry.run(getCtx ? getCtx() : ({ history, logout: () => {} } as ActionContext));
              } else if (modRef.current) {
                window.open(entry.path, '_blank', 'noopener,noreferrer');
              } else {
                history.push(entry.path);
              }
            }}
          >
            <Icon className="h-4 w-4" />
            <span>{renderWithHighlight(entry.label, query)}</span>
            {!isAction(entry) && entry.hint && (
              <span className="ml-auto text-xs text-muted-foreground">{entry.hint}</span>
            )}
          </CommandItem>
        );
      })}
    </CommandGroup>
  );
}
